import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiscoverFilters({ filters, onApply, onClose }) {
  const [minAge, setMinAge] = useState(filters.minAge || 18);
  const [maxAge, setMaxAge] = useState(filters.maxAge || 60);
  const [gender, setGender] = useState(filters.gender || 'all');
  const [maxDistance, setMaxDistance] = useState(filters.maxDistance || 25);

  const handleApply = () => {
    onApply({
      minAge,
      maxAge,
      gender: gender === 'all' ? null : gender,
      maxDistance
    });
    onClose();
  };

  const handleReset = () => {
    setMinAge(18);
    setMaxAge(60);
    setGender('all');
    setMaxDistance(25);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center">
      <div className="w-full max-w-md bg-[#111114] rounded-t-3xl sm:rounded-3xl border border-zinc-800 p-6 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-rose-500" />
            Filters
          </h2>
          <button
            data-testid="close-filters"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Distance */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-zinc-400">Max Distance</Label>
            <span className="text-white font-medium">{maxDistance} mi</span>
          </div>
          <Slider
            data-testid="distance-slider"
            value={[maxDistance]}
            onValueChange={(v) => setMaxDistance(v[0])}
            min={1}
            max={50}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between mt-1 text-zinc-600 text-xs">
            <span>1 mi</span>
            <span>50 mi</span>
          </div>
        </div>

        {/* Age Range */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-zinc-400">Age Range</Label>
            <span className="text-white font-medium">{minAge} - {maxAge}</span>
          </div>
          <div className="space-y-3">
            <div>
              <span className="text-zinc-500 text-xs">Min Age</span>
              <Slider
                data-testid="min-age-slider"
                value={[minAge]}
                onValueChange={(v) => { setMinAge(v[0]); if (v[0] > maxAge) setMaxAge(v[0]); }}
                min={18}
                max={99}
                step={1}
                className="w-full"
              />
            </div>
            <div>
              <span className="text-zinc-500 text-xs">Max Age</span>
              <Slider
                data-testid="max-age-slider"
                value={[maxAge]}
                onValueChange={(v) => { setMaxAge(v[0]); if (v[0] < minAge) setMinAge(v[0]); }}
                min={18}
                max={99}
                step={1}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Gender */}
        <div className="mb-8">
          <Label className="text-zinc-400 mb-2 block">Show Me</Label>
          <Select value={gender} onValueChange={setGender}>
            <SelectTrigger data-testid="filter-gender-select" className="bg-zinc-900 border-zinc-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">Everyone</SelectItem>
              <SelectItem value="male">Men</SelectItem>
              <SelectItem value="female">Women</SelectItem>
              <SelectItem value="non-binary">Non-binary</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <Button
            data-testid="reset-filters"
            onClick={handleReset}
            variant="outline"
            className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full"
          >
            Reset
          </Button>
          <Button
            data-testid="apply-filters"
            onClick={handleApply}
            className="flex-1 bg-rose-600 hover:bg-rose-500 text-white rounded-full"
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
}
